import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Users } from 'lucide-react';
import { problemDetail } from '@/shared/api/problem';
import {
  getGetApiV1CommitteeSettingsQueryKey,
  useGetApiV1CommitteeSettings,
  usePutApiV1CommitteeSettings,
} from '@/shared/api/generated/committee/committee';
import { useLocale } from '@/shared/lib/i18n/locale-context';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Panel } from '@/shared/ui/Panel';
import { QueryError } from '@/shared/ui/QueryError';

/**
 * The committee's size and voting window (§23.2).
 *
 * ⚠️ **§23.2 asks for this screen by name**, and gives the reason: *"the quorum figure should be
 * configurable from the dashboard, because 14 will be revisited after the first month of live use."*
 * Until 25 August 2026 the figure came from configuration — which is a deployment, during that first
 * month, at three in the morning, with a search waiting on votes that will not arrive.
 *
 * ⚠️ **The quorum is shown and never typed.** A majority is a fact about the size, and a field for
 * each is a pair that eventually disagree: a committee of forty approving on fourteen votes looks
 * correct on every screen in the system. It is displayed because it is what the administrator is
 * actually deciding — moving 27 to 40 is deciding that a search now needs 21 approvals.
 */
export function CommitteeSettings() {
  const { t } = useLocale();
  const queryClient = useQueryClient();

  const { data, isPending, isError, error, refetch } = useGetApiV1CommitteeSettings();

  const [size, setSize] = useState<string | null>(null);
  const [window, setWindow] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const save = usePutApiV1CommitteeSettings({
    mutation: {
      onSuccess: () => {
        setFailure(null);
        setSaved(true);
        setSize(null);
        setWindow(null);
        void queryClient.invalidateQueries({ queryKey: getGetApiV1CommitteeSettingsQueryKey() });
      },
      onError: (problem) => {
        setSaved(false);
        setFailure(problemDetail(problem) ?? t.committeeSettings.saveFailed);
      },
    },
  });

  // ⚠️ `null` means untouched, so the field follows the server until somebody types in it. Seeding
  // the state from the response instead would freeze whatever arrived first and quietly discard a
  // change made from another session.
  const sizeText = size ?? String(data?.committeeSize ?? '');
  const windowText = window ?? String(data?.votingWindowMinutes ?? '');

  const sizeValue = whole(sizeText);
  const windowValue = whole(windowText);

  const valid = sizeValue !== null && windowValue !== null;
  const changed =
    valid && (sizeValue !== data?.committeeSize || windowValue !== data?.votingWindowMinutes);

  // ⚠️ Computed here rather than read from the response, so the number moves as the field is typed
  // in. Reading it back from the server would show the OLD quorum beside the NEW size — the one
  // moment the two must not be allowed to disagree on screen.
  const quorum = sizeValue === null ? null : Math.max(1, Math.floor(sizeValue / 2) + 1);

  return (
    <div className="space-y-6">
      <PageHeader title={t.committeeSettings.title} lede={t.committeeSettings.subtitle} />

      {isError && <QueryError error={error} onRetry={() => void refetch()} />}

      {failure && (
        <p role="alert" className="rounded-lg bg-error-50 p-3 text-sm text-error-600">
          {failure}
        </p>
      )}

      {saved && !changed && (
        <p role="status" className="rounded-lg bg-success-50 p-3 text-sm text-success-600">
          {t.committeeSettings.saved}
        </p>
      )}

      <Panel title={t.committeeSettings.heading}>
        <form
          className="flex flex-col gap-5"
          onSubmit={(event) => {
            event.preventDefault();
            if (!valid || !changed) return;

            save.mutate({
              data: { committeeSize: sizeValue, votingWindowMinutes: windowValue },
            });
          }}
        >
          <div className="grid gap-5 sm:grid-cols-2">
            <Field
              label={t.committeeSettings.size}
              htmlFor="committee-size"
              hint={t.committeeSettings.sizeHint}
            >
              <input
                id="committee-size"
                inputMode="numeric"
                dir="ltr"
                disabled={isPending}
                className="h-11 w-full rounded-lg border border-gray-200 bg-white px-3 text-start text-sm tabular-nums outline-none focus:border-brand-300"
                value={sizeText}
                onChange={(event) => {
                  setSaved(false);
                  setSize(event.target.value);
                }}
              />
            </Field>

            <Field
              label={t.committeeSettings.window}
              htmlFor="voting-window"
              hint={t.committeeSettings.windowHint}
            >
              <input
                id="voting-window"
                inputMode="numeric"
                dir="ltr"
                disabled={isPending}
                className="h-11 w-full rounded-lg border border-gray-200 bg-white px-3 text-start text-sm tabular-nums outline-none focus:border-brand-300"
                value={windowText}
                onChange={(event) => {
                  setSaved(false);
                  setWindow(event.target.value);
                }}
              />
            </Field>
          </div>

          {/* ⚠️ The consequence of the number above, stated as a sentence rather than left for the
              administrator to halve in their head. It follows the field as it is typed. */}
          <div className="flex items-center gap-3 rounded-lg bg-gray-50 p-4">
            <Users className="h-5 w-5 shrink-0 text-gray-400" />
            <p className="text-sm text-gray-700">
              <span className="font-semibold tabular-nums">{quorum ?? '—'}</span>{' '}
              <span className="text-gray-500">
                {t.committeeSettings.quorum} — {t.committeeSettings.quorumNote}
              </span>
            </p>
          </div>

          <div className="flex justify-end">
            <Button type="submit" disabled={!valid || !changed || save.isPending}>
              {save.isPending ? t.committeeSettings.saving : t.committeeSettings.save}
            </Button>
          </div>
        </form>
      </Panel>
    </div>
  );
}

/**
 * ⚠️ Rejects anything that is not a plain positive whole number, rather than letting `Number()` turn
 * an empty field into zero — a committee of zero would approve a mission with no votes at all.
 */
function whole(text: string): number | null {
  if (!/^\d+$/.test(text.trim())) return null;

  const value = Number(text);

  return Number.isSafeInteger(value) && value > 0 ? value : null;
}
